import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';

import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useMetricMatrix from 'hooks/useMetricMatrix';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import to2DMatrixCells from '../to2DMatrixCells';
import heatmap from '../highchartOptions/heatmapOptions';

// Heatmap reuses the two-dimension metric-matrix pipeline (dimensionA rows x
// dimensionB columns, single metric) and turns the pivoted grid into
// [x, y, value] cells. skip2DRowDataFormatting keeps cell values raw/numeric.
const useHeatmapData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  isReady: boolean;
} => {
  const { openMenu } = useContext(DashboardContextMenuContext);

  // Derive (don't freeze in useState) — the gadget can mount before dimensionA
  // is set in the config modal, which would otherwise lock groupField to ''.
  const groupField = getGroupByField(chartDefinition);
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const { formatMetric } = useValueFormatters();
  const { columnDefs, rowData, isInitialLoading } = useMetricMatrix({
    chartDef: chartDefinition,
    skip2DRowDataFormatting: true,
  });
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'heatmap',
    chartDefinition,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, rowData]);

  useEffect(() => {
    const metricId = chartDefinition.series[0]?.metricId;
    if (!metricId || !groupField) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }

    const { xCategories, yCategories, cells } = to2DMatrixCells({
      columnDefs,
      rowData,
      groupField,
      excludeFields: chartDefinition.series.map((s) => s.metricId),
    });

    const points: [number, number, number][] = cells.map((cell) => [
      cell.x,
      cell.y,
      cell.value,
    ]);

    if (points.length === 0) {
      setIsNoDataToDisplay(true);
      setOptions(undefined);
      return;
    }

    const baseHeatmapOptions = heatmap({
      formatMetric,
      title: ' ',
      xCategories,
      yCategories,
      metricId,
      fontSize: 11,
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
    });

    setOptions({
      ...baseHeatmapOptions,
      series: [
        {
          type: 'heatmap',
          metricId,
          metricIds: [metricId],
          onDrillDown,
          borderWidth: 1,
          borderColor: 'var(--color-bg-primary)',
          animation: false,
          data: points,
        },
      ],
    } as any as Highcharts.Options);
    setIsNoDataToDisplay(false);
  }, [
    chartDefinition,
    columnDefs,
    formatMetric,
    groupField,
    onDrillDown,
    openMenu,
    rowData,
  ]);

  return {
    options,
    isNoData: isNoDataToDisplay,
    isReady: !isInitialLoading,
  };
};

export default useHeatmapData;
